import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Package, 
  Sparkles, 
  Settings, 
  CheckCircle, 
  AlertTriangle,
  Building2,
  Loader2,
  ArrowUpDown,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import type { TemplateDefinition, TemplateScopeItem, TemplateType, ConfigPhase } from '../types';

// Template color mapping for icon badges
const TEMPLATE_ICON_COLORS: Record<string, string> = {
  atlassian: 'bg-blue-600',
  custom: 'bg-gray-600',
  generic: 'bg-gray-600',
  servicenow: 'bg-red-600',
  ivanti: 'bg-green-600',
  'bmc-helix': 'bg-purple-600',
  freshservice: 'bg-teal-600',
};

interface TemplateManagerProps {
  accessToken: string;
  currentUser?: any; // Add currentUser to check subscription plan
}

interface ConsultingFirm {
  id: string;
  name: string;
  subdomain?: string;
  subscriptionPlan?: string;
}

export function TemplateManager({ accessToken, currentUser }: TemplateManagerProps) {
  const [templates, setTemplates] = useState<TemplateDefinition[]>([]);
  const [consultingFirms, setConsultingFirms] = useState<ConsultingFirm[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingFirms, setLoadingFirms] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateDefinition | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<TemplateDefinition | null>(null);
  const [parseText, setParseText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [currentFirm, setCurrentFirm] = useState<ConsultingFirm | null>(null);
  const [formData, setFormData] = useState<Partial<TemplateDefinition>>({
    name: '',
    description: '',
    type: 'custom',
    icon: 'TM',
    category: 'itsm',
    defaultScopeItems: [],
  });

  // Check if user can create templates (System Admin or Enterprise firm)
  const isSystemAdmin = currentUser?.role === 'systemAdmin' || currentUser?.role === 'system_admin';
  const isEnterpriseFirm = currentFirm?.subscriptionPlan === 'enterprise';
  const canCreateTemplates = isSystemAdmin || isEnterpriseFirm;

  useEffect(() => {
    loadTemplates();
    loadConsultingFirms();
    loadCurrentFirm();
  }, []);

  const loadCurrentFirm = async () => {
    if (!currentUser?.consultingFirmId) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${currentUser.consultingFirmId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCurrentFirm(data.firm);
      }
    } catch (error) {
      console.error('Error loading current firm:', error);
    }
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[TemplateManager] Loaded templates:', data.templates?.length || 0, 'templates');
        console.log('[TemplateManager] Template IDs:', data.templates?.map((t: any) => t.id));
        setTemplates(data.templates || []);
      } else {
        console.error('[TemplateManager] Failed to load templates:', response.status, response.statusText);
        toast.error('Failed to load templates');
      }
    } catch (error) {
      console.error('[TemplateManager] Error loading templates:', error);
      toast.error('Error loading templates');
    } finally {
      setLoading(false);
    }
  };

  const bootstrapSystemTemplates = async () => {
    if (!confirm('This will create all 6 system templates (Atlassian, ServiceNow, Ivanti, BMC Helix, Freshservice, Custom). Templates that already exist will be skipped. Continue?')) {
      return;
    }

    console.log('[TemplateManager] Starting bootstrap of system templates...');
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/templates/bootstrap-system`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[TemplateManager] Bootstrap response:', data);
        toast.success(data.message || 'System templates bootstrapped successfully');
        await loadTemplates();
      } else {
        const error = await response.json();
        console.error('[TemplateManager] Bootstrap error:', error);
        toast.error(error.error || 'Failed to bootstrap system templates');
      }
    } catch (error) {
      console.error('[TemplateManager] Error bootstrapping system templates:', error);
      toast.error('Error bootstrapping system templates');
    }
  };

  const fixTemplateProperties = async () => {
    if (!confirm('This will fix the isSystemTemplate property on existing templates. Continue?')) {
      return;
    }

    setFixing(true);
    console.log('[TemplateManager] Fixing template properties...');
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/templates/fix-system-property`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[TemplateManager] Fix response:', data);
        toast.success(data.message || 'Template properties fixed successfully');
        await loadTemplates();
      } else {
        const error = await response.json();
        console.error('[TemplateManager] Fix error:', error);
        toast.error(error.error || 'Failed to fix template properties');
      }
    } catch (error) {
      console.error('[TemplateManager] Error fixing template properties:', error);
      toast.error('Error fixing template properties');
    } finally {
      setFixing(false);
    }
  };

  const loadConsultingFirms = async () => {
    setLoadingFirms(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConsultingFirms(data.firms || []);
      } else {
        console.error('Failed to load consulting firms');
        // Don't show error toast - firm selector is optional
      }
    } catch (error) {
      console.error('Error loading consulting firms:', error);
    } finally {
      setLoadingFirms(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      type: 'custom',
      icon: 'TM',
      category: 'itsm',
      defaultScopeItems: [
        {
          id: 'scope-1',
          name: 'Service Desks / Projects',
          defaultValue: 1,
          description: 'Number of service desks or projects',
          required: true,
          order: 1,
        },
        {
          id: 'scope-2',
          name: 'Request Types (approx.)',
          defaultValue: 10,
          description: 'Approximate number of request types',
          required: true,
          order: 2,
        },
        {
          id: 'scope-3',
          name: 'Custom Workflows',
          defaultValue: 2,
          description: 'Number of custom workflows',
          required: true,
          order: 3,
        },
      ],
      features: [],
      isActive: true,
      isSystemTemplate: false,
      allowedFirmIds: [], // Empty = public/all firms
    });
    setDialogOpen(true);
  };

  const handleEditTemplate = (template: TemplateDefinition) => {
    setEditingTemplate(template);
    setFormData({
      ...template,
      type: template.type || 'custom',
      category: template.category || 'itsm',
      icon: template.icon || 'TM',
      isActive: template.isActive ?? true,
      isSystemTemplate: template.isSystemTemplate ?? false,
      allowedFirmIds: template.allowedFirmIds || [],
    });
    setDialogOpen(true);
  };

  const handleDuplicateTemplate = (template: TemplateDefinition) => {
    setEditingTemplate(null);
    setFormData({
      ...template,
      id: undefined,
      name: `${template.name} (Copy)`,
      type: template.type || 'custom',
      category: template.category || 'itsm',
      icon: template.icon || 'TM',
      isSystemTemplate: false,
      isActive: template.isActive ?? true,
      allowedFirmIds: template.allowedFirmIds || [], // Preserve access control
    });
    setDialogOpen(true);
  };

  const handleDeleteTemplate = (template: TemplateDefinition) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/templates/${templateToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Template deleted successfully');
        await loadTemplates();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error deleting template');
    } finally {
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSaveTemplate = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const url = editingTemplate
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/templates/${editingTemplate.id}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/templates`;

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated successfully' : 'Template created successfully');
        await loadTemplates();
        setDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template');
    }
  };

  const addScopeItem = () => {
    const newItem: TemplateScopeItem = {
      id: `scope-${Date.now()}`,
      name: '',
      defaultValue: 0,
      description: '',
      required: false,
      order: (formData.defaultScopeItems?.length || 0) + 1,
    };
    setFormData({
      ...formData,
      defaultScopeItems: [...(formData.defaultScopeItems || []), newItem],
    });
  };

  const updateScopeItem = (id: string, field: keyof TemplateScopeItem, value: any) => {
    setFormData({
      ...formData,
      defaultScopeItems: formData.defaultScopeItems?.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    });
  };

  const removeScopeItem = (id: string) => {
    setFormData({
      ...formData,
      defaultScopeItems: formData.defaultScopeItems?.filter(item => item.id !== id),
    });
  };

  const moveScopeItem = (id: string, direction: 'up' | 'down') => {
    const items = formData.defaultScopeItems || [];
    const index = items.findIndex(item => item.id === id);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    // Update order values
    newItems.forEach((item, idx) => {
      item.order = idx + 1;
    });

    setFormData({
      ...formData,
      defaultScopeItems: newItems,
    });
  };

  const handleParseText = async () => {
    if (!parseText.trim()) {
      toast.error('Please enter text to parse');
      return;
    }

    setParsing(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/templates/parse-scope-items`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ text: parseText }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        if (error.isQuotaError) {
          toast.error('AI parsing unavailable - OpenAI API quota exceeded. Please add items manually.');
        } else {
          toast.error(error.userMessage || 'Failed to parse text');
        }
        return;
      }

      const data = await response.json();
      const existingItems = formData.defaultScopeItems || [];
      const newItems = [...existingItems, ...data.scopeItems];
      
      setFormData({
        ...formData,
        defaultScopeItems: newItems,
      });

      setParseText('');
      toast.success(`Added ${data.scopeItems.length} scope items from parsed text`);
    } catch (error) {
      console.error('Error parsing text:', error);
      toast.error('Error parsing text');
    } finally {
      setParsing(false);
    }
  };

  const getTemplateIcon = (template: TemplateDefinition) => {
    return template.icon || 'TM';
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'itsm':
        return <Badge variant="default">ITSM</Badge>;
      case 'project-management':
        return <Badge variant="secondary">Project Management</Badge>;
      case 'custom':
        return <Badge variant="outline">Custom</Badge>;
      default:
        return <Badge>{category}</Badge>;
    }
  };

  const systemTemplates = templates.filter(t => t.isSystemTemplate);
  const customTemplates = templates.filter(t => !t.isSystemTemplate);

  console.log('[TemplateManager] System templates count:', systemTemplates.length);
  console.log('[TemplateManager] Custom templates count:', customTemplates.length);
  console.log('[TemplateManager] Show bootstrap button?', systemTemplates.length < 6);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl">Template Manager</h2>
          <p className="text-gray-600 mt-1">
            Create and manage templates for consulting firms to use with their clients
          </p>
        </div>
        <div className="flex gap-2">
          {systemTemplates.length < 6 && (
            <Button onClick={bootstrapSystemTemplates} variant="outline">
              <Package className="w-4 h-4 mr-2" />
              Bootstrap System Templates
            </Button>
          )}
          {templates.length > 0 && (systemTemplates.length !== 5 || customTemplates.length !== 1) && (
            <Button onClick={fixTemplateProperties} variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-50" disabled={fixing}>
              {fixing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  <Settings className="w-4 h-4 mr-2" />
                  Fix Template Categories
                </>
              )}
            </Button>
          )}
          {canCreateTemplates && (
            <Button onClick={handleCreateTemplate}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          )}
        </div>
      </div>

      {templates.length > 0 && (systemTemplates.length !== 5 || customTemplates.length !== 1) && (
        <Alert className="border-orange-500 bg-orange-50">
          <AlertTriangle className="w-4 h-4 text-orange-600" />
          <AlertDescription>
            <strong className="text-orange-900">Template Categories Need Fixing</strong>
            <br />
            Templates are not properly categorized. Expected: 5 System Templates and 1 Custom Template. 
            Current: {systemTemplates.length} System, {customTemplates.length} Custom.
            Click the <strong>"Fix Template Categories"</strong> button above to correct this.
          </AlertDescription>
        </Alert>
      )}

      <Alert>
        <Sparkles className="w-4 h-4" />
        <AlertDescription>
          <strong>Templates</strong> define default scope items, terminology, and configuration for different platforms 
          (Atlassian, ServiceNow, etc.). Consulting firms can select templates for each client company.
          {!isSystemAdmin && isEnterpriseFirm && (
            <><br /><br />
            <strong className="text-green-700">Enterprise Feature Unlocked:</strong> Your firm can create custom templates and duplicate existing templates to build your own template library.</>
          )}
          {!isSystemAdmin && !isEnterpriseFirm && currentFirm && (
            <><br /><br />
            <strong>Upgrade to Enterprise:</strong> Create custom templates tailored to your consulting methodology. <a href="/admin-panel" className="text-blue-600 hover:underline">Upgrade your subscription</a> to unlock template creation.</>
          )}
        </AlertDescription>
      </Alert>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">All Templates ({templates.length})</TabsTrigger>
            <TabsTrigger value="system">System Templates ({systemTemplates.length})</TabsTrigger>
            <TabsTrigger value="custom">Custom Templates ({customTemplates.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            <TemplatesList 
              templates={templates}
              onEdit={handleEditTemplate}
              onDuplicate={handleDuplicateTemplate}
              onDelete={handleDeleteTemplate}
            />
          </TabsContent>

          <TabsContent value="system" className="space-y-4">
            <TemplatesList 
              templates={systemTemplates}
              onEdit={handleEditTemplate}
              onDuplicate={handleDuplicateTemplate}
              onDelete={handleDeleteTemplate}
            />
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            <TemplatesList 
              templates={customTemplates}
              onEdit={handleEditTemplate}
              onDuplicate={handleDuplicateTemplate}
              onDelete={handleDeleteTemplate}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* Create/Edit Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogDescription>
              Define the default scope items and configuration for this template
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Template Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Atlassian Jira Cloud"
                    />
                  </div>

                  <div>
                    <Label htmlFor="type">Base Type *</Label>
                    <Select 
                      value={formData.type} 
                      onValueChange={(val) => setFormData({ ...formData, type: val as TemplateType })}
                    >
                      <SelectTrigger id="type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="atlassian">Atlassian</SelectItem>
                        <SelectItem value="servicenow">ServiceNow</SelectItem>
                        <SelectItem value="ivanti">Ivanti</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(val) => setFormData({ ...formData, category: val as any })}
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="itsm">ITSM</SelectItem>
                        <SelectItem value="project-management">Project Management</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="icon">Icon Label *</Label>
                    <Input
                      id="icon"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="TM"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this template is for and what it includes..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                    <Label htmlFor="isActive">Active</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="isSystemTemplate"
                      checked={formData.isSystemTemplate}
                      onCheckedChange={(checked) => setFormData({ ...formData, isSystemTemplate: checked })}
                    />
                    <Label htmlFor="isSystemTemplate">System Template (cannot be deleted)</Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Default Scope Items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Default Scope Items</CardTitle>
                    <CardDescription>
                      Define the scope items that will be pre-populated when using this template
                    </CardDescription>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addScopeItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* AI Text Parser */}
                <div className="mb-6 p-4 border rounded-lg bg-blue-50 dark:bg-blue-950 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                    <Label className="text-sm font-semibold">AI Quick Fill</Label>
                  </div>
                  <p className="text-xs text-gray-600">
                    Paste SOW text, scope description, or list of deliverables. AI will extract and create scope items automatically.
                  </p>
                  <Textarea
                    value={parseText}
                    onChange={(e) => setParseText(e.target.value)}
                    placeholder="Example: Configure 3 Service Desks with 25 request types across 5 teams. Set up SLA definitions for response and resolution. Create 10 custom workflows and 15 automation rules..."
                    rows={4}
                    disabled={parsing}
                  />
                  <Button
                    type="button"
                    onClick={handleParseText}
                    disabled={parsing || !parseText.trim()}
                    size="sm"
                    className="w-full"
                  >
                    {parsing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Parse & Add Scope Items
                      </>
                    )}
                  </Button>
                </div>

                <Separator className="my-4" />

                {/* Column Headers */}
                <div className="flex gap-2 items-center px-3 pb-2 text-xs text-gray-500 font-medium">
                  <div className="w-6"></div> {/* Up/Down buttons space */}
                  <div className="flex-1 grid grid-cols-12 gap-2">
                    <div className="col-span-3">Name *</div>
                    <div className="col-span-2">Default Qty</div>
                    <div className="col-span-2">Hours/Unit</div>
                    <div className="col-span-4">Description</div>
                    <div className="col-span-1 text-center">Required</div>
                  </div>
                  <div className="w-10"></div> {/* Delete button space */}
                </div>

                <div className="space-y-3">
                  {formData.defaultScopeItems?.map((item, index) => (
                    <div key={item.id} className="flex gap-2 items-start border rounded-lg p-3">
                      <div className="flex flex-col gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveScopeItem(item.id, 'up')}
                          disabled={index === 0}
                          className="h-6 w-6 p-0"
                        >
                          ▲
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveScopeItem(item.id, 'down')}
                          disabled={index === (formData.defaultScopeItems?.length || 0) - 1}
                          className="h-6 w-6 p-0"
                        >
                          ▼
                        </Button>
                      </div>

                      <div className="flex-1 grid grid-cols-12 gap-2">
                        <div className="col-span-3">
                          <Input
                            value={item.name}
                            onChange={(e) => updateScopeItem(item.id, 'name', e.target.value)}
                            placeholder="Scope item name"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.defaultValue}
                            onChange={(e) => updateScopeItem(item.id, 'defaultValue', parseInt(e.target.value) || 0)}
                            placeholder="Default value"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.estimatedHours || ''}
                            onChange={(e) => updateScopeItem(item.id, 'estimatedHours', parseInt(e.target.value) || undefined)}
                            placeholder="Est. hours"
                            title="Estimated hours per unit"
                          />
                        </div>
                        <div className="col-span-4">
                          <Input
                            value={item.description || ''}
                            onChange={(e) => updateScopeItem(item.id, 'description', e.target.value)}
                            placeholder="Description (optional)"
                          />
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          <Switch
                            checked={item.required}
                            onCheckedChange={(checked) => updateScopeItem(item.id, 'required', checked)}
                            title="Required"
                          />
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeScopeItem(item.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {(!formData.defaultScopeItems || formData.defaultScopeItems.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      No scope items defined. Click "Add Item" to create one.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Terminology (Optional) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Custom Terminology (Optional)</CardTitle>
                <CardDescription>
                  Customize the terms used in the UI for this template
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="term-project">Project / Space</Label>
                    <Input
                      id="term-project"
                      value={formData.terminology?.project || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        terminology: { ...formData.terminology, project: e.target.value }
                      })}
                      placeholder="e.g., Service Desk, Catalog"
                    />
                  </div>
                  <div>
                    <Label htmlFor="term-workflow">Workflow</Label>
                    <Input
                      id="term-workflow"
                      value={formData.terminology?.workflow || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        terminology: { ...formData.terminology, workflow: e.target.value }
                      })}
                      placeholder="e.g., Process, Flow"
                    />
                  </div>
                  <div>
                    <Label htmlFor="term-request">Request Type</Label>
                    <Input
                      id="term-request"
                      value={formData.terminology?.requestType || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        terminology: { ...formData.terminology, requestType: e.target.value }
                      })}
                      placeholder="e.g., Catalog Item, Issue Type"
                    />
                  </div>
                  <div>
                    <Label htmlFor="term-team">Team</Label>
                    <Input
                      id="term-team"
                      value={formData.terminology?.team || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        terminology: { ...formData.terminology, team: e.target.value }
                      })}
                      placeholder="e.g., Group, Assignment Group"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Access Control */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <div>
                    <CardTitle className="text-lg">Access Control</CardTitle>
                    <CardDescription>
                      Control which consulting firms can see and use this template
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingFirms ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading consulting firms...
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="public-access"
                          checked={!formData.allowedFirmIds || formData.allowedFirmIds.length === 0}
                          onCheckedChange={(checked) => {
                            setFormData({
                              ...formData,
                              allowedFirmIds: checked ? [] : []
                            });
                          }}
                        />
                        <div>
                          <Label htmlFor="public-access" className="cursor-pointer">
                            Available to All Consulting Firms
                          </Label>
                          <p className="text-xs text-gray-600 mt-0.5">
                            When enabled, all firms can see this template
                          </p>
                        </div>
                      </div>
                      <Badge variant={!formData.allowedFirmIds || formData.allowedFirmIds.length === 0 ? 'default' : 'secondary'}>
                        {!formData.allowedFirmIds || formData.allowedFirmIds.length === 0 ? 'Public' : 'Restricted'}
                      </Badge>
                    </div>

                    {formData.allowedFirmIds && formData.allowedFirmIds.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm">Accessible by Selected Firms</Label>
                        <div className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-900 max-h-48 overflow-y-auto space-y-1">
                          {consultingFirms
                            .filter(firm => formData.allowedFirmIds?.includes(firm.id))
                            .map(firm => (
                              <div key={firm.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-gray-500" />
                                  <span className="text-sm">{firm.name}</span>
                                  {firm.subdomain && (
                                    <Badge variant="outline" className="text-xs">
                                      {firm.subdomain}
                                    </Badge>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      allowedFirmIds: formData.allowedFirmIds?.filter(id => id !== firm.id)
                                    });
                                  }}
                                  className="h-6 w-6 p-0"
                                >
                                  ×
                                </Button>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {(!formData.allowedFirmIds || formData.allowedFirmIds.length === 0) && consultingFirms.length > 0 && (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                          Turn off "Available to All" to restrict access to specific firms
                        </AlertDescription>
                      </Alert>
                    )}

                    {formData.allowedFirmIds && formData.allowedFirmIds.length < consultingFirms.length && (
                      <div className="space-y-2">
                        <Label className="text-sm">Add Consulting Firms</Label>
                        <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto p-3 border rounded-lg">
                          {consultingFirms
                            .filter(firm => !formData.allowedFirmIds?.includes(firm.id))
                            .map(firm => (
                              <div
                                key={firm.id}
                                className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    allowedFirmIds: [...(formData.allowedFirmIds || []), firm.id]
                                  });
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-gray-500" />
                                  <span className="text-sm">{firm.name}</span>
                                  {firm.subdomain && (
                                    <Badge variant="outline" className="text-xs">
                                      {firm.subdomain}
                                    </Badge>
                                  )}
                                </div>
                                <Plus className="w-4 h-4 text-blue-600" />
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate}>
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Templates List Component
function TemplatesList({ 
  templates, 
  onEdit, 
  onDuplicate, 
  onDelete 
}: { 
  templates: TemplateDefinition[];
  onEdit: (template: TemplateDefinition) => void;
  onDuplicate: (template: TemplateDefinition) => void;
  onDelete: (template: TemplateDefinition) => void;
}) {
  if (templates.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <Package className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg mb-2">No templates found</h3>
          <p className="text-gray-600 text-sm">Create a new template to get started</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {templates.map((template) => (
        <Card key={template.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <span
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-lg text-base font-bold text-white flex-shrink-0 ${TEMPLATE_ICON_COLORS[template.type] || 'bg-gray-600'}`}
                >
                  {template.icon}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg">{template.name}</h3>
                    {template.isSystemTemplate && (
                      <Badge variant="secondary">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        System
                      </Badge>
                    )}
                    {!template.isActive && (
                      <Badge variant="outline">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                    {getCategoryBadge(template.category)}
                    {template.consultingFirmId && (
                      <Badge variant="outline">
                        <Building2 className="w-3 h-3 mr-1" />
                        Firm-Specific
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{template.defaultScopeItems?.length || 0} scope items</span>
                    <span>•</span>
                    <span>Type: {template.type}</span>
                    {template.terminology && (
                      <>
                        <span>•</span>
                        <span>Custom terminology</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {!template.allowedFirmIds || template.allowedFirmIds.length === 0 ? (
                      <Badge variant="default" className="text-xs">
                        <Globe className="w-3 h-3 mr-1" />
                        All Firms
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        <Building2 className="w-3 h-3 mr-1" />
                        {template.allowedFirmIds.length} Firm{template.allowedFirmIds.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(template)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDuplicate(template)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
                {!template.isSystemTemplate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(template)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function getCategoryBadge(category: string) {
  switch (category) {
    case 'itsm':
      return <Badge variant="default">ITSM</Badge>;
    case 'project-management':
      return <Badge variant="secondary">Project Management</Badge>;
    case 'custom':
      return <Badge variant="outline">Custom</Badge>;
    default:
      return <Badge>{category}</Badge>;
  }
}